import React, { useState, useEffect, useMemo, useCallback, Suspense, lazy } from 'react';
import DashboardSidebar from './components/Sidebar/DashboardSidebar';
import DashboardHeader from './components/Header/DashboardHeader';
import DataTable from './components/DataTable/DataTable';
import ToastNotification from './components/Toast/ToastNotification';
import apiFetch from './api/apiClient';
import {
  getOrderColumns,
  getRegistrationColumns,
  getUserColumns,
  getWorkshopColumns,
  getCouponColumns,
  getInquiryColumns,
  getInternshipColumns
} from './config/tableColumns';
import './App.css';

// Lazy-loaded views and modals for instant initial load (code-splitting)
const MasterBoardView = lazy(() => import('./components/MasterBoard/MasterBoardView'));
const TrafficView = lazy(() => import('./components/Traffic/TrafficView'));
const ReferralView = lazy(() => import('./components/Referrals/ReferralView'));
const AdminAuthView = lazy(() => import('./components/Auth/AdminAuthView'));
const CouponModal = lazy(() => import('./components/Coupons/CouponModal'));
const WorkshopModal = lazy(() => import('./components/Workshops/WorkshopModal'));
const InquiryDetailModal = lazy(() => import('./components/Inquiries/InquiryDetailModal'));
const LogoutModal = lazy(() => import('./components/LogoutModal/LogoutModal'));

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!localStorage.getItem('adminToken');
  });

  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const stored = localStorage.getItem('adminUser');
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  });

  const [activeTab, setActiveTab] = useState('masterboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCouponModalOpen, setIsCouponModalOpen] = useState(false);
  const [isWorkshopModalOpen, setIsWorkshopModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [editingWorkshop, setEditingWorkshop] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [toast, setToast] = useState(null);
  const [updatingUserId, setUpdatingUserId] = useState(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedInquiry(null);
    };
    if (selectedInquiry) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedInquiry]);

  useEffect(() => {
    const handleExpired = () => {
      setIsAuthenticated(false);
      setCurrentUser(null);
    };
    window.addEventListener('admin_session_expired', handleExpired);
    return () => window.removeEventListener('admin_session_expired', handleExpired);
  }, []);

  const fetchTabData = useCallback(async (tab) => {
    if (!isAuthenticated) return;
    if (tab === 'masterboard' || tab === 'traffic' || tab === 'settings' || tab === 'referrals') return;

    setLoading(true);
    const endpoint = tab === 'coupons'
      ? '/api/coupons'
      : (tab === 'inquiries'
        ? '/api/contact'
        : (tab === 'internships' ? '/api/internships' : `/api/dashboard/${tab}`));

    try {
      const response = await apiFetch(endpoint);
      let json = null;
      try {
        json = await response.json();
      } catch (e) {
        json = null;
      }
      if (json && json.success) {
        setTableData(json.data || []);
      } else {
        setTableData([]);
      }
    } catch (err) {
      setTableData([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchTabData(activeTab);
  }, [activeTab, fetchTabData]);

  const getHeaderTitle = () => {
    if (activeTab === 'masterboard') return 'Master Board Overview';
    if (activeTab === 'traffic') return 'Live Website Traffic Analytics';
    if (activeTab === 'inquiries') return 'Contact Form Messages & Inquiries';
    if (activeTab === 'internships') return 'Internship Applications & Resumes';
    return activeTab.charAt(0).toUpperCase() + activeTab.slice(1);
  };

  const handleCouponSaved = () => {
    fetchTabData('coupons');
  };

  const handleEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setIsCouponModalOpen(true);
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleToggleCoupon = async (id) => {
    try {
      await apiFetch(`/api/coupons/${id}/toggle`, { method: 'PUT' });
      fetchTabData('coupons');
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteCoupon = async (id) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    try {
      await apiFetch(`/api/coupons/${id}`, { method: 'DELETE' });
      fetchTabData('coupons');
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleInquiryStatus = async (id) => {
    try {
      await apiFetch(`/api/contact/${id}/status`, { method: 'PUT' });
      fetchTabData('inquiries');
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteInquiry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact message?')) return;
    try {
      await apiFetch(`/api/contact/${id}`, { method: 'DELETE' });
      fetchTabData('inquiries');
    } catch (err) {
      console.error(err);
    }
  };

  const handleDownloadResume = (resume, name) => {
    if (!resume || !resume.fileData) {
      alert('No resume file attached for this applicant.');
      return;
    }
    const link = document.createElement('a');
    link.href = resume.fileData;
    link.download = resume.fileName || `${name.replace(/\s+/g, '_')}_Resume`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteInternship = async (id) => {
    if (!window.confirm('Are you sure you want to delete this internship application?')) return;
    try {
      await apiFetch(`/api/internships/${id}`, { method: 'DELETE' });
      fetchTabData('internships');
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleUserStatus = async (user) => {
    const targetUserId = user._id || user.id;
    const isCurrentlyActive = user.isActive !== false && String(user.status || '').toLowerCase() !== 'inactive';
    const nextActive = !isCurrentlyActive;
    const nextStatusText = nextActive ? 'Active' : 'Inactive';

    if (!nextActive && currentUser && (currentUser.email === user.email || currentUser._id === targetUserId)) {
      if (!window.confirm("Warning: You are deactivating your own logged-in admin account! You will be blocked from logging in until reactivated. Proceed?")) {
        return;
      }
    }

    setUpdatingUserId(targetUserId);
    try {
      const response = await apiFetch(`/api/dashboard/users/${targetUserId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, isActive: nextActive, status: nextActive ? 'active' : 'inactive' })
      });
      const data = await response.json();
      if (data.success) {
        setTableData((prevData) =>
          prevData.map((item) => {
            const matches = item._id === targetUserId || item.id === targetUserId || item.email === user.email;
            if (matches) {
              return {
                ...item,
                isActive: nextActive,
                status: nextStatusText
              };
            }
            return item;
          })
        );
        setToast({
          type: nextActive ? 'success' : 'error',
          title: nextActive ? 'User Activated' : 'User Deactivated & Blocked',
          message: nextActive
            ? `${user.name || 'User'} is now Active. They can log in across all platforms.`
            : `${user.name || 'User'} is now Inactive. Login is blocked across website, student panel, & dashboard.`
        });
      } else {
        setToast({
          type: 'error',
          title: 'Action Failed',
          message: data.message || 'Failed to update user status.'
        });
      }
    } catch (err) {
      setToast({
        type: 'error',
        title: 'Network Error',
        message: 'Could not connect to server to update user status.'
      });
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleEditWorkshop = useCallback((workshop) => {
    setEditingWorkshop(workshop);
    setIsWorkshopModalOpen(true);
  }, []);

  const handleToggleWorkshopStatus = useCallback(async (workshop) => {
    const targetId = workshop.slug || workshop.id;
    const isCurrentlyInactive = String(workshop.status || '').toLowerCase() === 'inactive';
    const nextStatus = isCurrentlyInactive ? 'Active' : 'Inactive';

    try {
      const response = await apiFetch(`/api/workshops/${targetId}/toggle-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: targetId })
      });
      const data = await response.json();
      if (data.success) {
        setTableData((prev) =>
          prev.map((item) => {
            if (item.slug === workshop.slug || item.id === workshop.id || item.title === workshop.title) {
              return { ...item, status: nextStatus };
            }
            return item;
          })
        );
        setToast({
          type: nextStatus === 'Active' ? 'success' : 'info',
          title: `Workshop ${nextStatus}`,
          message: nextStatus === 'Active'
            ? `"${workshop.title}" is now Active and visible on the website.`
            : `"${workshop.title}" is now Inactive and hidden from the website.`
        });
      } else {
        setToast({
          type: 'error',
          title: 'Action Failed',
          message: data.message || 'Could not update status'
        });
      }
    } catch (err) {
      setToast({
        type: 'error',
        title: 'Network Error',
        message: 'Could not connect to server'
      });
    }
  }, []);

  const orderColumns = useMemo(() => getOrderColumns(), []);
  const registrationColumns = useMemo(() => getRegistrationColumns(), []);
  const workshopColumns = useMemo(
    () => getWorkshopColumns({
      onEditWorkshop: handleEditWorkshop,
      onToggleStatus: handleToggleWorkshopStatus
    }),
    [handleEditWorkshop, handleToggleWorkshopStatus]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const userColumns = useMemo(
    () => getUserColumns({ currentUser, updatingUserId, onToggleUserStatus: handleToggleUserStatus }),
    [currentUser, updatingUserId]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const couponColumns = useMemo(
    () => getCouponColumns({
      copiedCode,
      onCopyCode: handleCopyCode,
      onEditCoupon: handleEditCoupon,
      onToggleCoupon: handleToggleCoupon,
      onDeleteCoupon: handleDeleteCoupon
    }),
    [copiedCode]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const inquiryColumns = useMemo(
    () => getInquiryColumns({
      onSelectInquiry: setSelectedInquiry,
      onToggleInquiryStatus: handleToggleInquiryStatus,
      onDeleteInquiry: handleDeleteInquiry
    }),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const internshipColumns = useMemo(
    () => getInternshipColumns({
      onDownloadResume: handleDownloadResume,
      onDeleteInternship: handleDeleteInternship
    }),
    []
  );

  const handleLogoutClick = () => {
    setIsLogoutModalOpen(true);
  };

  const handleConfirmLogout = async () => {
    setLogoutLoading(true);
    try {
      await apiFetch('/api/auth/admin/logout', { method: 'POST' });
    } catch (err) {
      console.error('Logout API call error:', err);
    } finally {
      setLogoutLoading(false);
    }
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('token');
    sessionStorage.clear();
    setCurrentUser(null);
    setIsLogoutModalOpen(false);
    setIsAuthenticated(false);
  };

  if (!isAuthenticated) {
    return (
      <Suspense fallback={<div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: '#94a3b8', fontSize: '15px' }}>Loading Admin Portal...</div>}>
        <AdminAuthView
          onLoginSuccess={(userData) => {
            if (userData) setCurrentUser(userData);
            setIsAuthenticated(true);
          }}
        />
      </Suspense>
    );
  }

  return (
    <div className="dashboard-app-container">
      <DashboardSidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        currentUser={currentUser}
        onLogout={handleLogoutClick}
      />

      <main className="dashboard-main-area">
        <DashboardHeader
          title={getHeaderTitle()}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onLogout={handleLogoutClick}
        />

        <div className="dashboard-content-body">
          <Suspense fallback={<div style={{ padding: '60px 20px', textAlign: 'center', color: '#64748b' }}>Loading View...</div>}>
            {activeTab === 'masterboard' && <MasterBoardView />}
            {activeTab === 'traffic' && <TrafficView />}

            {activeTab === 'orders' && (
              <DataTable
                title="Customer Orders"
                columns={orderColumns}
                data={tableData}
                loading={loading}
                globalSearchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                searchPlaceholder="Search orders by customer, email or ID..."
              />
            )}

            {activeTab === 'registrations' && (
              <DataTable
                title="Workshop Registrations"
                columns={registrationColumns}
                data={tableData}
                loading={loading}
                globalSearchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                searchPlaceholder="Search registrations by name, phone or workshop..."
              />
            )}

            {activeTab === 'inquiries' && (
              <DataTable
                title="Contact Form Messages & Inquiries"
                columns={inquiryColumns}
                data={tableData}
                loading={loading}
                globalSearchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                searchPlaceholder="Search inquiries by name, email, phone or message..."
              />
            )}

            {activeTab === 'internships' && (
              <DataTable
                title="Internship Applications & Resumes"
                columns={internshipColumns}
                data={tableData}
                loading={loading}
                globalSearchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                searchPlaceholder="Search internship applications by applicant name, phone, city, or position..."
              />
            )}

            {activeTab === 'users' && (
              <DataTable
                title="Platform Users"
                columns={userColumns}
                data={tableData}
                loading={loading}
                globalSearchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                searchPlaceholder="Search users by name or email..."
              />
            )}

            {activeTab === 'workshops' && (
              <DataTable
                title="Yoga Workshops"
                columns={workshopColumns}
                data={tableData}
                loading={loading}
                globalSearchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                onAddNew={() => {
                  setEditingWorkshop(null);
                  setIsWorkshopModalOpen(true);
                }}
                searchPlaceholder="Search workshops..."
              />
            )}

            {activeTab === 'coupons' && (
              <DataTable
                title="Discount Coupons"
                columns={couponColumns}
                data={tableData}
                loading={loading}
                globalSearchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                onAddNew={() => {
                  setEditingCoupon(null);
                  setIsCouponModalOpen(true);
                }}
                searchPlaceholder="Search coupon code..."
              />
            )}

            {activeTab === 'referrals' && <ReferralView />}

            {activeTab === 'settings' && (
              <div className="dashboard-placeholder-card">
                <h3>Platform Settings</h3>
                <p>Configure admin preferences, notification webhooks, and payment gateway options.</p>
              </div>
            )}
          </Suspense>
        </div>
      </main>

      <Suspense fallback={null}>
        <CouponModal
          isOpen={isCouponModalOpen}
          onClose={() => {
            setIsCouponModalOpen(false);
            setEditingCoupon(null);
          }}
          onSave={handleCouponSaved}
          initialData={editingCoupon}
        />

        <WorkshopModal
          isOpen={isWorkshopModalOpen}
          initialData={editingWorkshop}
          onClose={() => {
            setIsWorkshopModalOpen(false);
            setEditingWorkshop(null);
          }}
          onSave={() => {
            fetchTabData('workshops');
            setToast({
              type: 'success',
              title: editingWorkshop ? 'Workshop Updated' : 'Workshop Created',
              message: editingWorkshop
                ? 'Workshop details have been updated and are live on the website!'
                : 'New workshop has been added to the database and is now live!'
            });
          }}
        />

        <LogoutModal
          isOpen={isLogoutModalOpen}
          onClose={() => setIsLogoutModalOpen(false)}
          onConfirm={handleConfirmLogout}
          loading={logoutLoading}
        />

        <InquiryDetailModal
          selectedInquiry={selectedInquiry}
          onClose={() => setSelectedInquiry(null)}
          onToggleStatus={handleToggleInquiryStatus}
          onDelete={handleDeleteInquiry}
        />
      </Suspense>

      {toast && (
        <ToastNotification
          type={toast.type}
          title={toast.title}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default App;
